import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import shippingService, { 
  RateRequestData as ServiceRateRequestData, 
  RateResponseData, 
  ShippingRate 
} from '../services/shippingService';
import { useState } from 'react';

// Override RateRequestData to make it more flexible with shipmentType
export interface RateRequestData extends Omit<ServiceRateRequestData, 'shipmentType'> {
  shipmentType: 'international' | 'domestic' | string;
}

// Options creator for tracking a shipment
export const getTrackShipmentQueryOptions = (trackingNumber: string, provider?: string) => ({
  queryKey: ['shipment', trackingNumber, provider],
  queryFn: () => shippingService.trackShipment(trackingNumber, provider),
  enabled: !!trackingNumber,
  staleTime: 1000 * 60 * 5, // 5 minutes
});

// Transform API response to our ShippingRate interface
const transformRatesToShippingRates = (response: RateResponseData): ShippingRate[] => {
  // For normal processing of successful responses only
  if (!response || !response.services || !Array.isArray(response.services) || response.services.length === 0) {
    return [];
  }

  return response.services.map(service => ({
    id: service.id,
    provider: service.provider,
    provider_display_name: service.provider_display_name,
    service_id: service.service_id,
    name: service.name,
    description: service.description,
    price: service.price,
    base_price: service.base_price,
    has_commission: service.has_commission,
    currency: service.currency,
    original_currency: service.original_currency,
    transit_days: service.transit_days,
    estimated_delivery_date: service.estimated_delivery_date,
    carrier_name: service.carrier_name,
    service_type: service.service_type,
    transit_time: service.transit_time,
    insurance_up_to: service.insurance_up_to,
    printing_required: service.printing_required,
    signature_required: service.signature_required,
    doorstep_delivery: service.doorstep_delivery,
    online_tracking: service.online_tracking,
    price_business_delivery: service.price_business_delivery,
    price_private_delivery: service.price_private_delivery
  }));
};

// Main shipping hook
export const useShipping = () => {
  const queryClient = useQueryClient();
  const [apiError, setApiError] = useState<RateResponseData | null>(null);
  const [apiWarning, setApiWarning] = useState<string | null>(null);
  const [shouldUnselect, setShouldUnselect] = useState<boolean>(false);

  // Shipping rates mutation - uses mutation since this is a POST that may change depending on input
  const getRatesMutation = useMutation({
    mutationFn: (data: RateRequestData) => shippingService.getRates(data as ServiceRateRequestData),
    onMutate: () => {
      // Clear prior status before starting a new request.
      setApiError(null);
      setApiWarning(null);
    },
    onSuccess: (data, variables) => {
      const nextRates = transformRatesToShippingRates(data);

      // For merged provider responses, `success: false` can still include valid services (partial success).
      if (data && data.success === false) {
        if (nextRates.length > 0) {
          setApiWarning(data.error_message || 'Some carriers failed to return rates.');
          setApiError(null);
        } else {
          setApiError(data);
        }
      }

      setShouldUnselect(false);
      queryClient.setQueryData(
        ['shipping-rates', JSON.stringify(variables)],
        nextRates
      );
    },
  });

  // Create label mutation
  const createLabelMutation = useMutation({
    mutationFn: shippingService.createLabel,
    onSuccess: (data) => {
      // Cache the label data
      queryClient.setQueryData(['shipping-label', data.id], data);
    },
  });

  // Validate address mutation
  const validateAddressMutation = useMutation({
    mutationFn: shippingService.validateAddress,
  });

  return {
    // Shipping rates
    getShippingRates: (data: RateRequestData) => 
      getRatesMutation.mutateAsync(data),
    shippingRates: 
      getRatesMutation.data ? transformRatesToShippingRates(getRatesMutation.data) : [],
    isLoadingRates: getRatesMutation.isPending,
    ratesError: getRatesMutation.error,
    apiError: apiError,
    apiWarning: apiWarning,
    shouldUnselect: shouldUnselect,
    resetShouldUnselect: () => setShouldUnselect(false),
    
    // Shipping label
    createLabel: (data: any) => createLabelMutation.mutateAsync(data),
    isCreatingLabel: createLabelMutation.isPending,
    labelError: createLabelMutation.error,
    
    // Address validation
    validateAddress: (data: any) => validateAddressMutation.mutateAsync(data),
    isValidatingAddress: validateAddressMutation.isPending,
    validationError: validateAddressMutation.error,
  };
};

// Shipment tracking hook
export const useTrackShipment = (trackingNumber: string, provider?: string) => {
  return useQuery(getTrackShipmentQueryOptions(trackingNumber, provider));
}; 
